import { apiFetch } from '@/lib/api';

export interface AgentChatEnsureDTO {
	linkId: string;
	boardArtifactId: string;
	settings: { require_write_confirmation?: boolean } | null;
	agentKey: string;
	eventName: string;
}

export interface AgentChatUploadedFileDTO {
	originalName: string;
	sessionPath: string | null;
	manifestId: string | null;
	fileId: string | null;
	/** Set for ZIP uploads: how many documents inside were registered. */
	registeredCount?: number;
	mapSessionPath?: string | null;
}

export type AgentMessageBlock = {
	type: string;
	text?: string;
	name?: string;
	[k: string]: unknown;
};

export interface AgentTranscriptMessage {
	role: 'user' | 'assistant' | string;
	content: string | AgentMessageBlock[];
	/** CRHQ returns camelCase; snake kept for tolerance across versions. */
	createdAt?: string;
	created_at?: string;
}

export interface AgentArtifactDTO {
	id: string;
	artifactType?: string;
	title?: string;
	content?: string;
	updatedAt?: string;
	updated_at?: string;
}

export interface AgentQueueItemDTO {
	id: string;
	message?: string;
	preview?: string;
	priority?: string;
	status?: string;
	created_at?: string;
	/** True when this was sent by a helper agent, not the user. */
	fromAgent?: boolean;
	fromSession?: string | null;
	/** Server-originated note (e.g. a pricing run finished) — never shown. */
	system?: boolean;
}

export interface AgentHelpersDTO {
	count: number;
	helpers: Array<{ status: string; since: string | null }>;
}

export interface AgentTranscribeResult {
	success: boolean;
	text?: string;
	/** Set when transcription failed but the audio is kept for a retry. */
	recordingId?: string;
	/** Plain message for the user when it failed. */
	message?: string;
}

async function readTranscribeResult(res: Response): Promise<AgentTranscribeResult> {
	const body = await res.json().catch(() => ({} as any));
	if (res.ok && body?.success) return { success: true, text: typeof body.text === 'string' ? body.text : '' };
	return {
		success: false,
		recordingId: typeof body?.recordingId === 'string' ? body.recordingId : undefined,
		message: body?.error?.message || 'Transcription failed.',
	};
}

// In-flight ensure dedupe: React StrictMode double-mounts effects in dev, which
// fired two concurrent ensures for the same event. Share one promise per event.
const ensureInFlight = new Map<string, Promise<AgentChatEnsureDTO>>();

export class AgentChatService {
	async ensure(tool: string, eventId: string): Promise<AgentChatEnsureDTO> {
		const key = `${tool}:${eventId}`;
		const existing = ensureInFlight.get(key);
		if (existing) return existing;
		const p = (async () => {
			const res = await apiFetch('/api/agent-chat/ensure', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ tool, contextKey: eventId }),
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.error?.message || 'Failed to connect to the Manifest Agent');
			}
			return (await res.json()).data as AgentChatEnsureDTO;
		})();
		ensureInFlight.set(key, p);
		try {
			return await p;
		} finally {
			ensureInFlight.delete(key);
		}
	}

	async helpers(linkId: string): Promise<AgentHelpersDTO> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/helpers`);
		if (!res.ok) return { count: 0, helpers: [] };
		return (await res.json()).data ?? { count: 0, helpers: [] };
	}

	async queue(linkId: string): Promise<AgentQueueItemDTO[]> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/queue`);
		if (!res.ok) return [];
		return (await res.json()).data.queue ?? [];
	}

	async transcript(linkId: string): Promise<{ messages: AgentTranscriptMessage[]; isProcessing: boolean }> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/transcript`);
		if (!res.ok) throw new Error('Failed to load the conversation');
		return (await res.json()).data;
	}

	async sendMessage(
		linkId: string,
		content: string,
		attachments?: AgentChatUploadedFileDTO[],
	): Promise<void> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/message`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				content,
				attachments: attachments?.map(a => ({
					originalName: a.originalName,
					sessionPath: a.sessionPath ?? '',
					manifestId: a.manifestId,
					fileId: a.fileId,
					registeredCount: a.registeredCount,
					mapSessionPath: a.mapSessionPath,
				})),
			}),
		});
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to send the message');
		}
	}

	async upload(linkId: string, files: File[]): Promise<{ files: AgentChatUploadedFileDTO[]; manifestId: string | null }> {
		const form = new FormData();
		for (const f of files) form.append('files', f, f.name);
		const res = await apiFetch(`/api/agent-chat/${linkId}/upload`, { method: 'POST', body: form });
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to upload files');
		}
		return (await res.json()).data;
	}

	/** Voice input: recording → text (relayed to the CRHQ transcription route). */
	async transcribe(linkId: string, audio: Blob): Promise<AgentTranscribeResult> {
		const form = new FormData();
		// Name it after the real format (Safari records mp4) — the speech service goes by the extension.
		const type = (audio.type || '').split(';')[0];
		const ext = type.includes('mp4') || type.includes('aac') ? 'm4a' : type.includes('ogg') ? 'ogg' : type.includes('wav') ? 'wav' : 'webm';
		form.append('audio', audio, `recording.${ext}`);
		const res = await apiFetch(`/api/agent-chat/${linkId}/transcribe`, { method: 'POST', body: form });
		return readTranscribeResult(res);
	}

	/** Retry a failed transcription (the server still holds the audio). */
	async retryTranscription(linkId: string, recordingId: string): Promise<AgentTranscribeResult> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/transcribe/retry/${encodeURIComponent(recordingId)}`, { method: 'POST' });
		return readTranscribeResult(res);
	}

	async stop(linkId: string): Promise<void> {
		await apiFetch(`/api/agent-chat/${linkId}/stop`, { method: 'POST' });
	}

	async cancelQueued(linkId: string, messageId: string): Promise<void> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/queue/${encodeURIComponent(messageId)}`, { method: 'DELETE' });
		if (!res.ok) throw new Error('Failed to cancel the queued message');
	}

	/** Stop the current task and run this queued message immediately. */
	async runQueuedNow(linkId: string, messageId: string): Promise<void> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/queue/${encodeURIComponent(messageId)}/push`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ mode: 'interrupt' }),
		});
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to run the queued message');
		}
	}

	async artifacts(linkId: string): Promise<AgentArtifactDTO[]> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/artifacts`);
		if (!res.ok) return [];
		return (await res.json()).data.artifacts ?? [];
	}

	async artifact(linkId: string, artifactId: string): Promise<AgentArtifactDTO | null> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/artifact/${encodeURIComponent(artifactId)}`);
		if (!res.ok) return null;
		return (await res.json()).data.artifact ?? null;
	}

	async rotate(linkId: string): Promise<{ linkId: string; boardArtifactId: string }> {
		const res = await apiFetch(`/api/agent-chat/${linkId}/rotate`, { method: 'POST' });
		if (!res.ok) throw new Error('Failed to start a fresh chat');
		return (await res.json()).data;
	}

	streamUrl(linkId: string): string {
		return `/api/agent-chat/${linkId}/stream`;
	}
}
